import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { PendingLinksService } from '../../../core/services/pending-links.service';
import {
  DEFAULT_PENDING_LINKS_CURRENT_PAGE,
  DEFAULT_PENDING_LINKS_PER_PAGE,
} from '../models/pending-link.model';
import * as PendingLinksActions from './pending-links.actions';
import {
  selectPendingLinksLastQuery,
  selectPendingLinksMeta,
} from './pending-links.selectors';

@Injectable()
export class PendingLinksEffects {
  private actions$ = inject(Actions);
  private pendingLinksService = inject(PendingLinksService);
  private store = inject(Store);

  loadPendingLinks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PendingLinksActions.loadPendingLinks),
      switchMap((action) =>
        this.pendingLinksService
          .getPendingLinks({
            page: action.page ?? DEFAULT_PENDING_LINKS_CURRENT_PAGE,
            perPage: action.perPage ?? DEFAULT_PENDING_LINKS_PER_PAGE,
            search: action.search,
            status: action.status,
            sourceSurface: action.sourceSurface,
            sort: action.sort ?? 'created_at',
            sortDirection: action.sortDirection ?? 'desc',
          })
          .pipe(
            map((response) =>
              PendingLinksActions.loadPendingLinksSuccess({
                pendingLinks: response.data,
                meta: response.meta,
              })
            ),
            catchError((error) =>
              of(
                PendingLinksActions.loadPendingLinksFailure({
                  error: error?.message ?? 'Failed to load pending links',
                })
              )
            )
          )
      )
    )
  );

  decidePendingLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PendingLinksActions.decidePendingLink),
      switchMap(({ action, payload }) => {
        let request$;

        switch (action) {
          case 'approve':
            request$ = this.pendingLinksService.approvePendingLink(payload);
            break;
          case 'approve_once':
            request$ = this.pendingLinksService.approveOncePendingLink(payload);
            break;
          case 'reject':
            request$ = this.pendingLinksService.rejectPendingLink(payload);
            break;
        }

        return request$.pipe(
          map(() => PendingLinksActions.decidePendingLinkSuccess({ action })),
          catchError((error) =>
            of(
              PendingLinksActions.decidePendingLinkFailure({
                error: error?.message ?? 'Action failed.',
              })
            )
          )
        );
      })
    )
  );

  bulkDecidePendingLinks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PendingLinksActions.bulkDecidePendingLinks),
      switchMap(({ payload }) =>
        this.pendingLinksService.bulkDecidePendingLinks(payload).pipe(
          map(() => PendingLinksActions.bulkDecidePendingLinksSuccess()),
          catchError((error) =>
            of(
              PendingLinksActions.bulkDecidePendingLinksFailure({
                error: error?.message ?? 'Bulk action failed.',
              })
            )
          )
        )
      )
    )
  );

  exportPendingLinks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PendingLinksActions.exportPendingLinks),
      switchMap(({ payload }) =>
        this.pendingLinksService.exportPendingLinks(payload).pipe(
          map((response) =>
            PendingLinksActions.exportPendingLinksSuccess({
              message: response?.message,
            })
          ),
          catchError((error) =>
            of(
              PendingLinksActions.exportPendingLinksFailure({
                error: error?.message ?? 'Something went wrong while processing an export.',
              })
            )
          )
        )
      )
    )
  );

  reloadAfterDecision$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PendingLinksActions.decidePendingLinkSuccess,
        PendingLinksActions.bulkDecidePendingLinksSuccess
      ),
      withLatestFrom(
        this.store.select(selectPendingLinksMeta),
        this.store.select(selectPendingLinksLastQuery)
      ),
      map(([, meta, lastQuery]) =>
        PendingLinksActions.loadPendingLinks({
          page: meta?.currentPage ?? DEFAULT_PENDING_LINKS_CURRENT_PAGE,
          perPage: meta?.perPage ?? DEFAULT_PENDING_LINKS_PER_PAGE,
          search: lastQuery.search,
          status: lastQuery.status,
          sourceSurface: lastQuery.sourceSurface,
          sort: lastQuery.sort,
          sortDirection: lastQuery.sortDirection,
        })
      )
    )
  );
}
